import { Alert, Button, Card, CardBody, Input, Select } from '@foundation/ui'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import styles from './settings.module.css'
import { AppLayout } from '@/components/AppLayout'
import { api } from '@/lib/api'
import { getAuthState, isAuthenticated } from '@/lib/auth-store'
import { toast } from '@/lib/toast'

export const Route = createFileRoute('/settings')({
  beforeLoad: () => {
    if (!isAuthenticated()) {
      throw redirect({ to: '/login' })
    }
  },
  component: SettingsPage,
})

const timezones = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern Time (US)' },
  { value: 'America/Chicago', label: 'Central Time (US)' },
  { value: 'America/Denver', label: 'Mountain Time (US)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US)' },
  { value: 'Europe/London', label: 'London' },
  { value: 'Europe/Paris', label: 'Paris' },
  { value: 'Asia/Tokyo', label: 'Tokyo' },
  { value: 'Asia/Shanghai', label: 'Shanghai' },
]

function SettingsPage() {
  const auth = getAuthState()
  const [activeTab, setActiveTab] = useState<'profile' | 'notifications' | 'security'>('profile')
  const [isSaving, setIsSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)

  // Profile form state
  const [profileData, setProfileData] = useState({
    name: auth.user?.name ?? '',
    email: auth.user?.email ?? '',
    timezone: 'UTC',
  })

  // Notification form state
  const [notificationData, setNotificationData] = useState({
    email_on_cancellation: true,
    email_on_win: true,
    email_on_connection_error: true,
    slack_on_cancellation: false,
    slack_on_win: false,
    slack_daily_digest: false,
  })

  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })
  const [passwordError, setPasswordError] = useState<string | null>(null)

  const handleSaveProfile = (e: React.FormEvent): void => {
    e.preventDefault()
    setIsSaving(true)
    setSaveSuccess(false)

    const profileUpdate: { name?: string; email?: string; timezone?: string } = {
      timezone: profileData.timezone,
    }
    if (profileData.name) {
      profileUpdate.name = profileData.name
    }

    api
      .updateProfile(profileUpdate)
      .then(() => {
        setSaveSuccess(true)
        toast.success('Profile updated successfully')
        setTimeout(() => setSaveSuccess(false), 3000)
      })
      .catch((err: unknown) => {
        toast.error(err instanceof Error ? err.message : 'Failed to save profile')
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  const handleSaveNotifications = (e: React.FormEvent): void => {
    e.preventDefault()
    setIsSaving(true)
    setSaveSuccess(false)

    api
      .updateNotifications(notificationData)
      .then(() => {
        setSaveSuccess(true)
        toast.success('Notification settings updated')
        setTimeout(() => setSaveSuccess(false), 3000)
      })
      .catch((err: unknown) => {
        toast.error(err instanceof Error ? err.message : 'Failed to save notifications')
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  const handleChangePassword = (e: React.FormEvent): void => {
    e.preventDefault()
    setPasswordError(null)

    if (passwordData.newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters')
      return
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('Passwords do not match')
      return
    }

    setIsSaving(true)
    setSaveSuccess(false)

    // Password change would go through a dedicated endpoint
    // For now, simulate the flow
    setTimeout(() => {
      setIsSaving(false)
      setSaveSuccess(true)
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
      toast.success('Password updated successfully')
      setTimeout(() => setSaveSuccess(false), 3000)
    }, 1000)
  }

  const tabs = [
    { id: 'profile' as const, label: 'Profile', icon: '👤' },
    { id: 'notifications' as const, label: 'Notifications', icon: '🔔' },
    { id: 'security' as const, label: 'Security', icon: '🔒' },
  ]

  return (
    <AppLayout title="Settings">
      <div className={styles.container}>
        {/* Tabs */}
        <div className={styles.tabs}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className={styles.tabIcon}>{tab.icon}</span>
              <span className={styles.tabLabel}>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Success Alert */}
        {saveSuccess && (
          <Alert variant="success" className={styles.successAlert}>
            Settings saved successfully!
          </Alert>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <Card>
            <CardBody>
              <h2 className={styles.sectionTitle}>Profile Settings</h2>
              <p className={styles.sectionDescription}>
                Update your personal information and preferences.
              </p>

              <form className={styles.form} onSubmit={handleSaveProfile}>
                <Input
                  label="Name"
                  type="text"
                  value={profileData.name}
                  onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  placeholder="Your name"
                />

                <Input
                  label="Email"
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                  placeholder="you@example.com"
                  disabled
                />
                <p className={styles.fieldHint}>Email cannot be changed.</p>

                <Select
                  label="Default Timezone"
                  value={profileData.timezone}
                  onChange={(e) => setProfileData({ ...profileData, timezone: e.target.value })}
                  options={timezones}
                />

                <div className={styles.formActions}>
                  <Button type="submit" variant="primary" loading={isSaving}>
                    Save Changes
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <Card>
            <CardBody>
              <h2 className={styles.sectionTitle}>Notification Settings</h2>
              <p className={styles.sectionDescription}>
                Configure how and when you receive alerts about cancellations and wins.
              </p>

              <form className={styles.form} onSubmit={handleSaveNotifications}>
                <div className={styles.toggleGroup}>
                  <div className={styles.toggle}>
                    <label className={styles.toggleLabel}>
                      <input
                        type="checkbox"
                        checked={notificationData.email_on_cancellation}
                        onChange={(e) =>
                          setNotificationData({
                            ...notificationData,
                            email_on_cancellation: e.target.checked,
                          })
                        }
                      />
                      <span className={styles.toggleSwitch} />
                      <div className={styles.toggleText}>
                        <span className={styles.toggleTitle}>Email on Cancellation</span>
                        <span className={styles.toggleDescription}>
                          Receive an email when a subscriber cancels
                        </span>
                      </div>
                    </label>
                  </div>

                  <div className={styles.toggle}>
                    <label className={styles.toggleLabel}>
                      <input
                        type="checkbox"
                        checked={notificationData.email_on_win}
                        onChange={(e) =>
                          setNotificationData({
                            ...notificationData,
                            email_on_win: e.target.checked,
                          })
                        }
                      />
                      <span className={styles.toggleSwitch} />
                      <div className={styles.toggleText}>
                        <span className={styles.toggleTitle}>Email on Win-Back</span>
                        <span className={styles.toggleDescription}>
                          Get notified when a cancelled subscriber reactivates
                        </span>
                      </div>
                    </label>
                  </div>

                  <div className={styles.toggle}>
                    <label className={styles.toggleLabel}>
                      <input
                        type="checkbox"
                        checked={notificationData.email_on_connection_error}
                        onChange={(e) =>
                          setNotificationData({
                            ...notificationData,
                            email_on_connection_error: e.target.checked,
                          })
                        }
                      />
                      <span className={styles.toggleSwitch} />
                      <div className={styles.toggleText}>
                        <span className={styles.toggleTitle}>Connection Error Alerts</span>
                        <span className={styles.toggleDescription}>
                          Alert when Stripe connection has issues
                        </span>
                      </div>
                    </label>
                  </div>

                  <div className={styles.toggle}>
                    <label className={styles.toggleLabel}>
                      <input
                        type="checkbox"
                        checked={notificationData.slack_on_cancellation}
                        onChange={(e) =>
                          setNotificationData({
                            ...notificationData,
                            slack_on_cancellation: e.target.checked,
                          })
                        }
                      />
                      <span className={styles.toggleSwitch} />
                      <div className={styles.toggleText}>
                        <span className={styles.toggleTitle}>Slack on Cancellation</span>
                        <span className={styles.toggleDescription}>
                          Send cancellation alerts to Slack
                        </span>
                      </div>
                    </label>
                  </div>

                  <div className={styles.toggle}>
                    <label className={styles.toggleLabel}>
                      <input
                        type="checkbox"
                        checked={notificationData.slack_on_win}
                        onChange={(e) =>
                          setNotificationData({
                            ...notificationData,
                            slack_on_win: e.target.checked,
                          })
                        }
                      />
                      <span className={styles.toggleSwitch} />
                      <div className={styles.toggleText}>
                        <span className={styles.toggleTitle}>Slack on Win-Back</span>
                        <span className={styles.toggleDescription}>
                          Send win-back notifications to Slack
                        </span>
                      </div>
                    </label>
                  </div>

                  <div className={styles.toggle}>
                    <label className={styles.toggleLabel}>
                      <input
                        type="checkbox"
                        checked={notificationData.slack_daily_digest}
                        onChange={(e) =>
                          setNotificationData({
                            ...notificationData,
                            slack_daily_digest: e.target.checked,
                          })
                        }
                      />
                      <span className={styles.toggleSwitch} />
                      <div className={styles.toggleText}>
                        <span className={styles.toggleTitle}>Slack Daily Digest</span>
                        <span className={styles.toggleDescription}>
                          Receive a daily summary of cancellations and wins in Slack
                        </span>
                      </div>
                    </label>
                  </div>
                </div>

                <div className={styles.formActions}>
                  <Button type="submit" variant="primary" loading={isSaving}>
                    Save Changes
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <>
            <Card className={styles.securityCard}>
              <CardBody>
                <h2 className={styles.sectionTitle}>Change Password</h2>
                <p className={styles.sectionDescription}>
                  Update your password to keep your account secure.
                </p>

                {passwordError && (
                  <Alert variant="danger" className={styles.alert}>
                    {passwordError}
                  </Alert>
                )}

                <form className={styles.form} onSubmit={handleChangePassword}>
                  <Input
                    label="Current Password"
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) =>
                      setPasswordData({ ...passwordData, currentPassword: e.target.value })
                    }
                    placeholder="Enter current password"
                    required
                  />

                  <Input
                    label="New Password"
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) =>
                      setPasswordData({ ...passwordData, newPassword: e.target.value })
                    }
                    placeholder="At least 8 characters"
                    required
                  />

                  <Input
                    label="Confirm New Password"
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) =>
                      setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                    }
                    placeholder="Confirm new password"
                    required
                  />

                  <div className={styles.formActions}>
                    <Button type="submit" variant="primary" loading={isSaving}>
                      Update Password
                    </Button>
                  </div>
                </form>
              </CardBody>
            </Card>

            <Card className={styles.dangerCard}>
              <CardBody>
                <h2 className={styles.dangerTitle}>Danger Zone</h2>
                <p className={styles.dangerDescription}>
                  Once you delete your account, there is no going back. All your sequences,
                  cancellation data, and settings will be permanently removed.
                </p>
                <Button
                  variant="danger"
                  onClick={() => {
                    const confirmed = window.confirm(
                      'Are you sure you want to delete your account? This action cannot be undone.'
                    )
                    if (confirmed) {
                      api.deleteAccount('DELETE').catch((err: unknown) => {
                        toast.error(err instanceof Error ? err.message : 'Failed to delete account')
                      })
                    }
                  }}
                >
                  Delete Account
                </Button>
              </CardBody>
            </Card>
          </>
        )}
      </div>
    </AppLayout>
  )
}
